import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import PageHeader from "@/components/feature/PageHeader";
import SectionHeading from "@/components/base/SectionHeading";
import { principles, site, stats } from "@/mocks/site";
import { testimonials } from "@/mocks/testimonials";

const positions = [
  {
    icon: "ri-search-eye-line",
    title: "替你先看一遍",
    desc: "每一个进入推荐名单的群，我们都会先观察一段时间，确认它不是靠少数人刷屏撑起来的热闹。",
  },
  {
    icon: "ri-filter-3-line",
    title: "把噪音挡在门外",
    desc: "收费群、拉人头群、广告群一律不收录。推荐名单保持克制，宁缺毋滥。",
  },
  {
    icon: "ri-book-2-line",
    title: "教你方法，不止给链接",
    desc: "除了排行，我们更希望你把选群的方法学会，这样即使群组更迭也能自己判断。",
  },
];

export default function About() {
  const jsonLd = useMemo(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: `关于${site.name}`,
        description: site.description,
        inLanguage: "zh-CN",
        url: `${origin}/about`,
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: site.name,
        description: site.description,
        email: site.email,
        url: `${origin}/`,
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${origin}/` },
          { "@type": "ListItem", position: 2, name: "关于我们", item: `${origin}/about` },
        ],
      },
    ];
  }, []);

  useSeo({
    title: `关于我们 - ${site.name}的定位、内容原则与联系方式`,
    description:
      "吃瓜群精选专注51吃瓜群的推荐与排行，坚持只收录免费交流群、活跃度优先、口碑来源于真实反馈。这里介绍我们的网站定位、内容原则、用户口碑与联系方式。",
    keywords: "关于吃瓜群精选,51吃瓜群网站,吃瓜群推荐原则,吃瓜群口碑",
    path: "/about",
    jsonLd,
  });

  return (
    <>
      <PageHeader
        eyebrow="关于我们"
        title={`${site.name}：把选群这件事做扎实`}
        desc="我们不是群链接的搬运工，而是一个愿意花时间替你先看一遍的内容站。下面是我们是谁、我们坚持什么，以及如何联系我们。"
        crumbs={[{ label: "首页", to: "/" }, { label: "关于我们" }]}
      />

      <section className="w-full bg-background-50 px-4 py-12 md:px-6 md:py-16">
        <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-8 lg:grid-cols-2 lg:gap-14">
          <div className="h-64 w-full overflow-hidden rounded-lg border border-background-200 md:h-80">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20flat%20editorial%20illustration%20of%20a%20cozy%20reading%20corner%20with%20a%20laptop%2C%20notebook%20and%20a%20bowl%20of%20fruit%2C%20soft%20cream%20and%20coral%20palette%20with%20green%20accents%2C%20calm%20friendly%20minimal%20vector%20art%2C%20no%20text&width=1200&height=800&seq=chigua-about-01&orientation=landscape"
              alt="吃瓜群精选网站介绍配图：温馨的阅读与整理场景"
              className="h-full w-full object-cover object-top"
            />
          </div>
          <div>
            <SectionHeading
              eyebrow="网站定位"
              title="一个帮普通人省时间的吃瓜群指南"
              desc="互联网上关于「吃瓜群」的信息很多，但大多是随手转发的链接，没人告诉你哪个群真的活跃、哪个群广告满天飞。吃瓜群精选要解决的，正是这道筛选题。"
            />
            <p className="mt-5 text-sm leading-relaxed text-foreground-600 md:text-base">
              我们围绕「51吃瓜群」这个搜索需求，持续观察不同方向群组的活跃度与氛围，把观察结果整理成一份可以长期参考的推荐名单。
              你不需要加十几个群慢慢试，先读方法、再看排行，就能大幅减少踩坑的概率。
            </p>
            <div className="mt-6 grid grid-cols-2 gap-4">
              {stats.map((item) => (
                <div key={item.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
                  <p className="font-heading text-xl text-foreground-950 md:text-2xl">
                    {item.value}
                    <span className="ml-0.5 font-label text-xs text-foreground-500">{item.suffix}</span>
                  </p>
                  <p className="mt-1 text-xs text-foreground-600">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 px-4 py-12 md:px-6 md:py-16" aria-label="我们在做什么">
        <div className="mx-auto max-w-6xl">
          <SectionHeading eyebrow="我们在做什么" title="三件小事，长期坚持" />
          <div className="mt-9 grid grid-cols-1 gap-4 md:grid-cols-3">
            {positions.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <h3 className="mt-4 font-heading text-lg text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 px-4 py-12 md:px-6 md:py-16" aria-label="内容原则">
        <div className="mx-auto max-w-6xl">
          <SectionHeading
            eyebrow="内容原则"
            title="我们怎么判断一个群值不值得推荐"
            desc="这四条原则贯穿在每一次推荐与排行更新中，也是我们和普通链接分享最大的区别。"
          />
          <div className="mt-9 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {principles.map((item) => (
              <div
                key={item.title}
                className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <div>
                  <h3 className="font-label text-base font-medium text-foreground-900">{item.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 px-4 py-12 md:px-6 md:py-16" aria-label="用户口碑">
        <div className="mx-auto max-w-6xl">
          <SectionHeading eyebrow="用户口碑" title="瓜友们的真实反馈" />
          <div className="mt-9 grid grid-cols-1 gap-4 md:grid-cols-2">
            {testimonials.slice(0, 2).map((item) => (
              <figure key={item.id} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className="ri-double-quotes-l"></i>
                </span>
                <blockquote className="mt-4 text-sm leading-relaxed text-foreground-700">{item.content}</blockquote>
                <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                  <span className="h-10 w-10 shrink-0 overflow-hidden rounded-full bg-background-100">
                    <img src={item.avatar} alt={`${item.name}的头像`} className="h-full w-full object-cover object-top" />
                  </span>
                  <span>
                    <span className="block font-label text-sm font-medium text-foreground-900">{item.name}</span>
                    <span className="block text-xs text-foreground-500">{item.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 px-4 py-12 md:px-6 md:py-16" aria-label="联系方式">
        <div className="mx-auto max-w-6xl">
          <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
            <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr]">
              <div className="p-6 md:p-10">
                <SectionHeading eyebrow="联系方式" title="想推荐群组或有建议？欢迎告诉我们" />
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
                  如果你发现了氛围不错、更新稳定的群，或者对推荐标准有不同看法，都欢迎通过下面的方式联系我们。
                  我们尤其欢迎带上「你为什么推荐它」的具体理由，这会让评估更高效。
                </p>
                <ul className="mt-7 flex flex-col gap-4">
                  <li className="flex items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className="ri-mail-line text-lg"></i>
                    </span>
                    <span>
                      <span className="block text-xs text-foreground-500">邮件联系</span>
                      <span className="block break-all font-label text-sm text-foreground-900">{site.email}</span>
                    </span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                      <i className="ri-wechat-line text-lg"></i>
                    </span>
                    <span>
                      <span className="block text-xs text-foreground-500">微信联系</span>
                      <span className="block font-label text-sm text-foreground-900">{site.wechat}</span>
                    </span>
                  </li>
                  <li className="flex items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                      <i className="ri-refresh-line text-lg"></i>
                    </span>
                    <span>
                      <span className="block text-xs text-foreground-500">更新节奏</span>
                      <span className="block font-label text-sm text-foreground-900">{site.updateNote}</span>
                    </span>
                  </li>
                </ul>
              </div>
              <div className="flex flex-col justify-center gap-4 bg-accent-800 p-6 text-accent-50 md:p-10">
                <h3 className="font-heading text-lg text-background-50 md:text-xl">先从排行榜开始</h3>
                <p className="text-sm leading-relaxed text-accent-100/90">
                  榜单已经按综合分数排好，并支持按主题筛选。挑出几个候选群后，再对照指南慢慢体验就好。
                </p>
                <div className="flex flex-col gap-3">
                  <Link
                    to="/ranking"
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
                  >
                    查看群组排行
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                  <Link
                    to="/guide"
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-accent-400/60 px-5 py-2.5 font-label text-sm text-accent-50 transition-colors hover:bg-accent-900/50"
                  >
                    阅读选群指南
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}